import React from 'react';
import { Slider, Box } from '@mui/material';
import ValueLabel from './ValueLabel';

function toNumber(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function computeLikertSpec(question) {
  const opts = Array.isArray(question?.options) ? question.options : [];
  const cfg = question?.likertConfig || {};
  const rangeProp = Array.isArray(question?.range) ? question.range : null;
  const labelsProp = Array.isArray(question?.labels) ? question.labels : null;

  // Prefer config labels if provided; then question.labels; then options
  const cfgLabels = Array.isArray(cfg.labels) ? cfg.labels : null;
  const chosenLabels = cfgLabels ?? labelsProp ?? opts;

  let min; let max; let labels;
  if ((typeof cfg.start === 'number' || typeof cfg.start === 'string') && (typeof cfg.levels === 'number' || typeof cfg.levels === 'string')) {
    const start = toNumber(cfg.start) ?? 1;
    const levels = toNumber(cfg.levels) ?? (Array.isArray(chosenLabels) ? chosenLabels.length : 0);
    min = start;
    max = start + levels - 1;
    labels = chosenLabels;
  } else if (rangeProp && rangeProp.length === 2) {
    const r0 = toNumber(rangeProp[0]);
    const r1 = toNumber(rangeProp[1]);
    min = r0 ?? 0;
    max = r1 ?? (min + ((Array.isArray(chosenLabels) ? chosenLabels.length : 1) - 1));
    labels = chosenLabels;
  } else {
    // No explicit config or range; infer from options
    const numericOpts = opts.map(o => toNumber(o));
    const allNumeric = opts.length > 0 && numericOpts.every(n => n !== null);
    if (allNumeric) {
      min = Math.min(...numericOpts);
      max = Math.max(...numericOpts);
      labels = chosenLabels;
    } else {
      min = 0;
      max = Math.max(0, opts.length - 1);
      labels = chosenLabels;
    }
  }
  return { min, max, step: 1, labels: Array.isArray(labels) ? labels : [] };
}

function isObjMark(l) {
  return l && typeof l === 'object' && 'value' in l && 'label' in l;
}

function buildMarks(min, max, step, labels) {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  // Support object-based labels: [{value, label}]
  if (labels.some(isObjMark)) {
    return labels
      .filter(isObjMark)
      .map(l => ({ value: toNumber(l.value) ?? min, label: (l.label ?? '').toString() }))
      .filter(m => m.value >= min && m.value <= max);
  }
  const totalSteps = Math.floor((max - min) / step) + 1;
  // If labels cover every step, render a mark at each step; show label text where provided (sparse labels allowed)
  if (labels.length === totalSteps) {
    return labels.map((label, idx) => ({ value: min + idx * step, label: (label ?? '').toString() }));
  }
  // Otherwise, if we have at least 2 labels, render endpoints only (prefer first/last non-empty)
  if (labels.length >= 2) {
    const first = (labels.find(l => (l ?? '').toString().trim() !== '') ?? labels[0]) || '';
    const last = (labels.slice().reverse().find(l => (l ?? '').toString().trim() !== '') ?? labels[labels.length - 1]) || '';
    return [
      { value: min, label: first },
      { value: max, label: last }
    ];
  }
  return [];
}

export default function LikertSlider({ question, value, onChange }) {
  const { min, max, step, labels } = computeLikertSpec(question);
  const defaultValue = Math.round((min + max) / 2);
  const questionDefault = toNumber(question?.defaultValue);
  const numericValue = typeof value === 'number' ? value : (toNumber(value) ?? (questionDefault ?? defaultValue));
  const marks = buildMarks(min, max, step, labels);

  return (
    <Box sx={{ px: 2 }}>
      <Slider
        value={numericValue}
        min={min}
        max={max}
        step={step}
        marks={marks}
        onChange={(_, newValue) => {
          const n = Array.isArray(newValue) ? newValue[0] : newValue;
          onChange?.(typeof n === 'number' ? n : numericValue);
        }}
        valueLabelDisplay="auto"
        components={{ ValueLabel }}
        valueLabelFormat={(v) => {
          if (labels.some(isObjMark)) {
            const match = labels.find(l => isObjMark(l) && toNumber(l.value) === v);
            const lbl = match?.label;
            if (lbl !== undefined && (lbl ?? '').toString().trim() !== '') return lbl;
          }
          const idx = Math.round((v - min) / step);
          const lbl = labels[idx];
          return (lbl !== undefined && (lbl ?? '').toString().trim() !== '') ? lbl : v;
        }}
        sx={{
          width: '90%',
          marginTop: 2,
          '& .MuiSlider-markLabel': {
            fontSize: '0.6rem',
            whiteSpace: 'normal',
            textAlign: 'center',
            wordWrap: 'break-word',
            maxWidth: '60px',
            transform: 'translateX(-50%)',
          },
        }}
      />
    </Box>
  );
}
